import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import EmployeeService from '@frontend/AppSettings/services/EmployeeService';
import MatterService from '@frontend/AppSettings/services/MatterService';
import { useBidCapabilities } from '@frontend/BidSheets/useBidCapabilities';
import PhasePlanningSection from './PhasePlanningSection';

/**
 * The Team tab: who is on a converted pipeline project, and how they get there.
 *
 * Conversion sets ONE person — `matters.employeeid`, the lead — and that field is optional. The
 * legacy TeamWidget that manages the rest lives on the regular MatterDetails chrome, which a
 * pipeline matter does not render (MatterDetails branches to this tab set instead), so before
 * this tab a converted project had no team surface at all: you could not see who was on it, you
 * could not add anyone, and because the Projects list's "My Projects" tab filters on `userIds`,
 * everyone but the lead saw "No projects found".
 *
 * Deliberately a pipeline-LOCAL component over the SHARED matter-team API, not a reuse of
 * `TeamWidget`: the regular chrome serves live legal/engineering users today and nothing here
 * may change what they see. Storage, endpoints and semantics stay identical; the markup is this
 * folder's own CSS grammar.
 *
 * The model (verified, not assumed):
 *  - STORAGE  `matters.assoc_employees`, a JSON array of employee-id strings.
 *  - READ     `GET /matter_by_id/<id>` hydrates it into `associated_employees`; the LEAD arrives
 *             separately as `employeeid` + `employee_details` and is composed in here.
 *  - WRITE    `PUT /update_associated_employees/<id>` — `{operation, employee_ids}`.
 *  - GATE     the server's `can_access_matter` (A32) already refused every non-member on the READ:
 *             permish 1/2 see all matters, everyone else only a matter they lead or are associated
 *             on. So the roster itself is open to anyone who reached this screen.
 *
 * MUTATE_BIDS gates the ADD/REMOVE affordance — the same capability `FieldTimesheetTab` calls "the
 * PM's authority" for Verify Day, and the one conversion itself requires. It is an affordance gate
 * only: `PUT /update_associated_employees` is tenant-scoped and carries no ownership check, which
 * is a pre-existing property of the SHARED endpoint this build deliberately does not change (see
 * plans/completed/bid-project-integration/pipeline-project-team.md). Hiding the controls is honesty about who should be
 * doing this, not a security boundary.
 */
interface ProjectTeamTabProps {
  matterid: string;
}

interface TeamMember {
  /** Stable list key; `lead-`/`member-` prefixed so a lead who is also listed cannot collide. */
  key: string;
  employeeId: string;
  name: string;
  email?: string;
  isLead: boolean;
}

const toName = (first: unknown, last: unknown): string =>
  `${first ?? ''} ${last ?? ''}`.trim();

/**
 * Two different all-null rows reach this component, and both must be dropped:
 *
 *  - a matter with NO associates at all still returns one placeholder object, because the read is
 *    `json_agg` over a `LEFT JOIN LATERAL … ON TRUE` — proven, not assumed
 *    (`test_conversion_service.py::test_an_unknown_caller_seeds_NOTHING_rather_than_a_ghost_id`);
 *  - a real id with no matching employee (another tenant's, a deleted person) hydrates the same
 *    way, and so does a LEAD whose `employees` join misses.
 *
 * Rendered, either is a blank row wearing a Remove button. The `null null` arm is carried over
 * from the legacy widget's `isValidName` for the case where the driver hands back the literal
 * STRINGS: today `toName` coalesces nulls to '' and the length check does the work, but the two
 * surfaces should not disagree about what a real name is.
 */
const isRealName = (name: string): boolean => name.length > 0 && !/^null\s+null$/i.test(name);

const ProjectTeamTab = ({ matterid }: ProjectTeamTabProps): JSX.Element => {
  const queryClient = useQueryClient();
  const capabilities = useBidCapabilities();
  const mayManage = capabilities?.capabilities.includes('mutate_bids') ?? false;

  const [selectedId, setSelectedId] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  // A DEDICATED key, deliberately NOT MatterDetails' ['ProjectDetailRoute', matterid]. That key
  // is the ROUTING DECISION; sharing a matter key across that boundary caused an infinite
  // mount/unmount loop (AUTH-N07), and invalidating it on every add would put the whole screen
  // back through the branch. One extra GET, on a tab that only mounts when opened.
  const team = useQuery({
    queryKey: ['ProjectTeam', matterid],
    queryFn: () => MatterService.getMatterDetails(matterid),
  });

  // The shared directory key/queryFn (ConvertToProjectModal, ContactsPage, CreateProjectsPage) —
  // fetched only for someone who can actually act on it.
  const directory = useQuery({
    queryKey: ['Employees'],
    queryFn: () => EmployeeService.getEmployees(),
    enabled: mayManage,
  });

  const members = useMemo<TeamMember[]>(() => {
    const detail = team.data;
    if (!detail) return [];
    const rows: TeamMember[] = [];

    // The lead is composed from the matter row, NOT written into assoc_employees: duplicating a
    // fact `matters.employeeid` already holds would desync the moment the lead changes, and the
    // access gate and the My-Projects filter already match the lead independently.
    const lead = detail.employee_details;
    const leadName = toName(lead?.firstname, lead?.lastname);
    if (detail.employeeid && lead && isRealName(leadName)) {
      rows.push({
        key: `lead-${detail.employeeid}`,
        employeeId: String(detail.employeeid),
        name: leadName,
        email: lead.email ?? undefined,
        isLead: true,
      });
    }

    (detail.associated_employees ?? []).forEach((emp) => {
      const id = String(emp?.employeeid ?? '');
      if (!id || id === 'null') return;
      const name = toName(emp?.firstname, emp?.lastname);
      if (!isRealName(name)) return;
      rows.push({
        key: `member-${id}`,
        employeeId: id,
        name,
        email: emp?.email ?? undefined,
        isLead: false,
      });
    });

    return rows;
  }, [team.data]);

  const onTeam = useMemo(() => new Set(members.map((m) => m.employeeId)), [members]);

  // The directory is the CAPITALISED EmployeesSchema (EmployeeID/FirstName/Email) while
  // associated_employees is lowercase — comparing the wrong pair silently offers people who are
  // already on the project.
  //
  // Sorted by NAME: EmployeeService.getEmployees() sorts by EmployeeID, which is a UUID, so the
  // list arrives in an order no human can navigate.
  const available = useMemo(
    () => (directory.data ?? [])
      .filter((emp) => !onTeam.has(emp.EmployeeID))
      .filter((emp) => isRealName(toName(emp.FirstName, emp.LastName)))
      .sort((a, b) => toName(a.FirstName, a.LastName).localeCompare(toName(b.FirstName, b.LastName))),
    [directory.data, onTeam],
  );

  // A dropdown alone does not survive a real employee directory; the legacy modal this replaces
  // filters on name/email/id for the same reason.
  const matches = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return available;
    return available.filter((emp) => `${toName(emp.FirstName, emp.LastName)} ${emp.Email ?? ''} ${emp.EmployeeID}`
      .toLowerCase().includes(term));
  }, [available, search]);

  // Derived, not stored: narrowing the search past the current pick must not leave a selection the
  // user can no longer see (and could then submit blind).
  const effectiveSelection = matches.some((e) => e.EmployeeID === selectedId) ? selectedId : '';

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['ProjectTeam', matterid] });
    // The phase-crew panel below reads this same roster to compute `on_team`; without this, removing
    // a member leaves their phase assignment rendering as still-on-team until a manual re-expand —
    // the stranded case the panel exists to surface would go unshown.
    void queryClient.invalidateQueries({ queryKey: ['ProjectPhaseCrew', matterid] });
  };

  const addMember = useMutation({
    mutationFn: (employeeId: string) =>
      MatterService.updateAssociatedEmployees(matterid, 'add', [employeeId]),
    onSuccess: () => {
      setSelectedId('');
      setError(null);
      void refresh();
    },
    onError: (err: unknown) =>
      setError((err as Error)?.message || 'Could not add that person to the project.'),
  });

  const removeMember = useMutation({
    mutationFn: (employeeId: string) =>
      MatterService.updateAssociatedEmployees(matterid, 'remove', [employeeId]),
    onSuccess: () => {
      setError(null);
      void refresh();
    },
    onError: (err: unknown) =>
      setError((err as Error)?.message || 'Could not remove that person from the project.'),
  });

  /** Destructive and un-undoable — it revokes that person's access to the project and drops it out
   *  of their My Projects. The legacy widget this re-implements confirms for the same reason. */
  const confirmRemove = (member: TeamMember): void => {
    if (!window.confirm(`Remove ${member.name} from this project?`)) return;
    removeMember.mutate(member.employeeId);
  };

  return (
    <div className="pipeline-team">
      <section className="pipeline-panel">
        <h2 className="pipeline-panel-title">Project team</h2>
        <p className="pipeline-panel-subtitle">
          Everyone on this project. Team members can open it from My Projects and record time
          against it; the lead is the project&apos;s owner and is set at conversion.
        </p>

        {team.isLoading && <p className="pipeline-muted">Loading the team…</p>}
        {/* Only when there is nothing to show. React Query keeps the last good data across a
            failed REFETCH, and "could not be loaded" printed above a populated table would be a
            lie about the table; the softer line below covers that case. */}
        {team.isError && members.length === 0 && (
          <p className="pipeline-muted">This project&apos;s team could not be loaded.</p>
        )}
        {team.isError && members.length > 0 && (
          <p className="pipeline-muted">This list could not be refreshed — it may be out of date.</p>
        )}

        {team.isSuccess && members.length === 0 && (
          <p className="pipeline-muted">
            Nobody is on this project yet — not even a lead.
            {mayManage
              ? ' Add someone below so it appears in their My Projects.'
              : ' Until someone is added, it will not appear in anyone’s My Projects.'}
          </p>
        )}

        {members.length > 0 && (
          <table className="pipeline-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                {mayManage && <th aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.key}>
                  <td>
                    {member.name}
                    {member.isLead && <span className="pipeline-team-lead">Lead</span>}
                  </td>
                  <td className="pipeline-line-cell">{member.email || '—'}</td>
                  {mayManage && (
                    <td>
                      {/* The lead has no remove control: they are `matters.employeeid`, not a row
                          in assoc_employees, so this endpoint could not remove them anyway.
                          Changing the lead is the convert dialog's job, not this tab's. */}
                      {!member.isLead && (
                        <button
                          type="button"
                          className="pipeline-secondary-btn"
                          onClick={() => confirmRemove(member)}
                          // Only the row being removed goes busy — `isPending` alone greyed out
                          // every Remove button on the project.
                          disabled={removeMember.isPending
                            && removeMember.variables === member.employeeId}
                          aria-label={`Remove ${member.name}`}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {error && <p className="pipeline-form-error" role="alert">{error}</p>}

        {/* `team.isSuccess`, not just `mayManage`: while the roster is loading or errored,
            `onTeam` is empty and the picker would offer people who are ALREADY on the project —
            including the LEAD, whose id must never reach assoc_employees (it lives on
            matters.employeeid; a copy would show them twice and desync when the lead changes). */}
        {mayManage && team.isSuccess && (
          <form
            className="pipeline-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (effectiveSelection) addMember.mutate(effectiveSelection);
            }}
          >
            <div className="pipeline-form-row">
              <label>
                Find an employee
                <input
                  type="search"
                  value={search}
                  placeholder="Name, email or ID…"
                  onChange={(e) => setSearch(e.target.value)}
                />
              </label>
              <label>
                Add a team member
                <select
                  value={effectiveSelection}
                  onChange={(e) => setSelectedId(e.target.value)}
                  disabled={directory.isLoading || directory.isError || matches.length === 0}
                >
                  <option value="">
                    {directory.isLoading
                      ? 'Loading employees…'
                      : directory.isError
                        // NOT "everyone is already on this project": a failed fetch that reads as
                        // an empty result tells the user a falsehood they cannot act on.
                        ? 'Employee list unavailable — reload to try again'
                        : available.length === 0
                          ? 'Everyone is already on this project'
                          : matches.length === 0
                            ? 'No employee matches that search'
                            : 'Select an employee…'}
                  </option>
                  {matches.map((emp) => (
                    <option key={emp.EmployeeID} value={emp.EmployeeID}>
                      {toName(emp.FirstName, emp.LastName)}
                      {emp.Email ? ` — ${emp.Email}` : ''}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="pipeline-form-actions">
              <button
                type="submit"
                className="pipeline-action-btn"
                disabled={!effectiveSelection || addMember.isPending}
              >
                Add to project
              </button>
            </div>
          </form>
        )}
      </section>

      {/* The OPTIONAL phase-planning layer, below the matter roster (Build R, plan
          team-bid-parity-and-fleet-registry): the old crew and equipment panels UNIFIED —
          one section, the bid's rail idiom, ONE network gate (collapsed fetches nothing).
          The crew pool is exactly the roster above (the server's subset wall refuses anyone
          off-team); the equipment pool is the tagged fleet. */}
      <PhasePlanningSection
        matterid={matterid}
        teamMembers={members.map((m) => ({ employeeId: m.employeeId, name: m.name }))}
        mayManage={mayManage}
      />
    </div>
  );
};

export default ProjectTeamTab;
